// src/pages/products/ProductList.js - VERSION AVEC CHARTE GRAPHIQUE SECURECONTROL
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Palette de couleurs SecureControl
const COLORS = {
  primary: '#0B3C5D',     // Bleu Sécurité
  secondary: '#1E88C8',   // Bleu Tech
  accent: '#4FC3F7',      // Bleu Clair
  darkText: '#2E2E2E',    // Gris Foncé
  lightBg: '#F5F7FA',     // Gris Clair
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3',
  successLight: '#e8f5e9',
  dangerLight: '#ffebee',
  warningLight: '#fff3e0',
  infoLight: '#e3f2fd'
};

const statusLabel = (statut) => {
  switch (statut) {
    case 1:
      return { text: "Actif", className: "badge bg-success" };
    case 0:
    default:
      return { text: "Inactif", className: "badge bg-secondary" };
  }
};

const ProductList = () => {
  const navigate = useNavigate();
  const [produits, setProduits] = useState([]);
  const [typeProduits, setTypeProduits] = useState([]);
  const [filteredProduits, setFilteredProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [serverError, setServerError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  
  // États pour l'erreur d'abonnement
  const [abonnementError, setAbonnementError] = useState(false);
  const [abonnementMessage, setAbonnementMessage] = useState("");
  
  // États pour les filtres
  const [search, setSearch] = useState("");
  const [filterStatut, setFilterStatut] = useState("");
  const [filterType, setFilterType] = useState("");
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // États pour le modal de confirmation
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalAction, setModalAction] = useState("");

  // Fonction pour réinitialiser l'erreur d'abonnement
  const resetAbonnementError = () => {
    setAbonnementError(false);
    setAbonnementMessage("");
  };

  useEffect(() => {
    loadTypeProduits();
    fetchProduits();
  }, []);

  useEffect(() => {
    if (infoMsg) {
      const timer = setTimeout(() => setInfoMsg(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [infoMsg]);

  // Appliquer les filtres
  useEffect(() => {
    if (produits.length > 0) {
      let filtered = [...produits];

      if (search) {
        filtered = filtered.filter(produit =>
          produit.libele.toLowerCase().includes(search.toLowerCase())
        );
      }

      if (filterStatut) {
        filtered = filtered.filter(produit => 
          String(produit.statut) === filterStatut
        );
      }

      if (filterType) {
        filtered = filtered.filter(produit => 
          String(produit.id_type_produit) === filterType
        );
      }

      setFilteredProduits(filtered);
      setCurrentPage(1);
    }
  }, [produits, search, filterStatut, filterType]);

  // Fonction pour charger les types de produits
  const loadTypeProduits = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Token d'authentification manquant.");
        setLoadingTypes(false);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/stock/type_produit/list_type_produit.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.data?.success && response.data?.data) {
        setTypeProduits(response.data.data.map(type => ({
          value: type.id.toString(),
          label: type.type
        })));
      }
    } catch (err) {
      console.error("Erreur chargement types produits:", err);
      
      // Vérifier si c'est une erreur 403 avec code ABONNEMENT_REQUIS
      if (err.response?.status === 403 && err.response?.data?.code === 'ABONNEMENT_REQUIS') {
        setAbonnementError(true);
        setAbonnementMessage(err.response.data.message || "Abonnement inactif");
        setTypeProduits([]);
      } else {
        setServerError("Erreur lors du chargement des types de produits.");
      }
    } finally {
      setLoadingTypes(false);
    }
  };

  const typeProduitLabel = (typeValue) => {
    if (!typeValue) return "";
    const str = String(typeValue).trim();
    if (!str) return "";

    let found = typeProduits.find((opt) => opt.value === str);
    if (!found) {
      found = typeProduits.find((opt) => opt.label.toLowerCase() === str.toLowerCase());
    }
    if (!found && typeProduits.some(opt => opt.label === str)) {
      return str;
    }
    return found ? found.label : typeValue;
  };

  const fetchProduits = async () => {
    setLoading(true);
    setServerError("");
    resetAbonnementError();
    
    try {
      const url = `${API_BASE_URL}/stock/produits/list_produit.php`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
      });
      
      if (response.data?.success) {
        const produitsList = response.data.data?.produits || [];
        setProduits(produitsList);
        setFilteredProduits(produitsList);
        
        if (produitsList.length === 0) {
          setInfoMsg("Aucun produit trouvé.");
        }
      } else {
        setServerError(response.data?.message || "Impossible de charger la liste des produits.");
      }
    } catch (err) {
      console.error("Erreur chargement produits:", err);
      
      // Vérifier si c'est une erreur 403 avec code ABONNEMENT_REQUIS
      if (err.response?.status === 403 && err.response?.data?.code === 'ABONNEMENT_REQUIS') {
        setAbonnementError(true);
        setAbonnementMessage(err.response.data.message || "Abonnement inactif");
        setProduits([]);
        setFilteredProduits([]);
      } else {
        handleFetchError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFetchError = (err) => {
    if (err.response?.status === 401) {
      setServerError("Session expirée. Veuillez vous reconnecter.");
      setTimeout(() => navigate("/login"), 2000);
    } else if (err.response?.status === 403) {
      setServerError("Permission refusée.");
    } else if (err.response?.status === 500) {
      setServerError("Erreur serveur. Veuillez contacter l'administrateur.");
    } else if (err.request) {
      setServerError("Serveur inaccessible. Vérifiez votre connexion.");
    } else {
      setServerError("Erreur technique lors du chargement des produits.");
    }
  };

  const openConfirmModal = (produit, action) => {
    setSelectedProduct(produit);
    setModalAction(action);
    setShowConfirmModal(true);
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
    setSelectedProduct(null);
    setModalAction("");
  };

  const handleToggleStatus = async () => {
    if (!selectedProduct) {
      closeConfirmModal();
      return;
    }

    const newStatus = selectedProduct.statut === 1 ? 0 : 1;
    const action = newStatus === 1 ? "activer" : "désactiver";
    
    setUpdatingId(selectedProduct.id);
    resetAbonnementError();

    try {
      const url = `${API_BASE_URL}/stock/produits/desactiver.php`;
      const response = await axios.put(
        url,
        { id_produit: selectedProduct.id, statut_produit: newStatus },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.success) {
        setInfoMsg(response.data.message || `Produit ${action} avec succès.`);
        fetchProduits();
      } else {
        setServerError(response.data?.message || `Impossible de ${action} le produit.`);
      }
    } catch (err) {
      console.error(`Erreur ${action} produit :`, err);
      
      // Vérifier si c'est une erreur 403 avec code ABONNEMENT_REQUIS
      if (err.response?.status === 403 && err.response?.data?.code === 'ABONNEMENT_REQUIS') {
        setAbonnementError(true);
        setAbonnementMessage(err.response.data.message || "Abonnement inactif");
      } else {
        setServerError(`Erreur technique lors de ${action} le produit.`);
      }
    } finally {
      setUpdatingId(null);
      closeConfirmModal();
    }
  };

  const formatDateShort = (dateString) => {
    if (!dateString || dateString === 'null' || dateString === '0000-00-00 00:00:00') return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR');
    } catch (e) {
      return dateString;
    }
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProduits.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProduits.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSearch("");
    setFilterStatut("");
    setFilterType("");
  };

  const hasActiveFilters = () => {
    return search || filterStatut || filterType;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Pas besoin de faire un appel API car les filtres sont appliqués côté client
    // via useEffect
  };

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Liste des produits</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/dashboard"  style={{ color: COLORS.primary }}>Tableau de bord</a>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Produits</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="card-title mb-1" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px',
                  width: '100%'
                }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-box-seam"></i>
                    </div>
                    Liste des produits
                  </div>
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Gérez vos produits et surveillez les niveaux de stock
                </p>
              </div>
              
              <button
                className="btn fw-semibold"
                onClick={() => navigate("/products/new")}
                disabled={loadingTypes}
                style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.primary,
                    borderColor: COLORS.primary,
                    borderRadius: '8px',
                    padding: '10px 24px'
                }}
              >
                {loadingTypes ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Chargement...
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus-lg me-2 fw-bold"></i>
                    Nouveau produit
                  </>
                )}
              </button>
            </div>

            {/* Affichage de l'erreur d'abonnement */}
            {abonnementError && (
              <div className="alert alert-warning alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#fff3e0',
                borderColor: COLORS.warning,
                color: '#ff8f00',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.warning}`,
                whiteSpace: "pre-line"
              }}>
                <div className="d-flex align-items-start">
                  <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                  <div className="flex-grow-1">
                    {abonnementMessage}
                    <div className="d-flex gap-2 justify-content-center mt-3">
                      <a 
                        href="/abonnements/create"
                        className="btn fw-semibold"
                        style={{
                          backgroundColor: '#4caf50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 20px',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
                      >
                        <i className="bi bi-box-arrow-up-right"></i>
                        Souscrire à un abonnement
                      </a>
                      <button
                        className="btn fw-semibold"
                        onClick={() => {
                          setAbonnementError(false);
                          setAbonnementMessage("");
                        }}
                        style={{
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 20px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
                      >
                        Fermer
                      </button>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => {
                      setAbonnementError(false);
                      setAbonnementMessage("");
                    }}
                  ></button>
                </div>
              </div>
            )}

            {serverError && !abonnementError && (
              <div className="alert alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{serverError}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setServerError("")}
                  ></button>
                </div>
              </div>
            )}
            
            {infoMsg && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: COLORS.success,
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.success}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-check-circle me-2"></i>
                  <div className="flex-grow-1">{infoMsg}</div>
                  <button type="button" className="btn-close" onClick={() => setInfoMsg("")}></button>
                </div>
              </div>
            )}

            {/* Filtres dans une carte */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card" style={{ 
                  backgroundColor: '#E3F2FD',
                  border: 'none',
                  borderRadius: '8px'
                }}>
                  <div className="card-body py-3">
                    <div className="row g-3 align-items-center">
                      {/* Recherche par libellé */}
                      <div className="col-md-4">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary,
                            height: '100%'
                          }}>
                            <i className="bi bi-search"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Rechercher par libellé..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText,
                              height: '38px'
                            }}
                          />
                        </div>
                      </div>
                      
                      {/* Filtre Statut */}
                      <div className="col-md-3">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary,
                            height: '100%'
                          }}>
                            <i className="bi bi-funnel"></i>
                          </span>
                          <select
                            className="form-select"
                            value={filterStatut}
                            onChange={(e) => setFilterStatut(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText,
                              height: '38px'
                            }}
                          >
                            <option value="">Tous les statuts</option>
                            <option value="1">Actif</option>
                            <option value="0">Inactif</option>
                          </select>
                        </div>
                      </div>

                      {/* Filtre Type */}
                      <div className="col-md-3">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary,
                            height: '100%'
                          }}>
                            <i className="bi bi-tag"></i>
                          </span>
                          <select
                            className="form-select"
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            disabled={loadingTypes}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText,
                              height: '38px'
                            }}
                          >
                            <option value="">Tous les types</option>
                            {loadingTypes ? (
                              <option value="" disabled>Chargement des types...</option>
                            ) : (
                              typeProduits.map(type => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                              ))
                            )}
                          </select>
                        </div>
                      </div>

                      {/* Boutons de filtres */}
                      <div className="col-md-2">
                        <div className="d-flex gap-2" style={{ height: '38px' }}>
                          <button 
                            type="button" 
                            className="btn fw-semibold flex-fill d-flex align-items-center justify-content-center"
                            onClick={handleSearch}
                            style={{
                              color: COLORS.white,
                              backgroundColor: COLORS.secondary,
                              borderColor: COLORS.secondary,
                              borderRadius: '6px',
                              height: '100%',
                              minHeight: '38px',
                              padding: '0 12px',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            <i className="bi bi-search"></i>
                          </button>
                          <button 
                            type="button" 
                            className="btn fw-semibold flex-fill d-flex align-items-center justify-content-center"
                            onClick={resetFilters}
                            style={{
                              color: COLORS.primary,
                              borderColor: COLORS.primary,
                              backgroundColor: 'transparent',
                              borderRadius: '6px',
                              height: '100%',
                              minHeight: '38px',
                              padding: '0 12px',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#E3F2FD';
                              e.target.style.borderColor = COLORS.secondary;
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                              e.target.style.borderColor = COLORS.primary;
                            }}
                          >
                            <i className="bi bi-x-lg"></i>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Résumé des filtres */}
                    <div className="mt-3 d-flex justify-content-between align-items-center">
                      <div className="text-muted small" style={{ color: COLORS.darkText }}>
                        <i className="bi bi-info-circle me-1"></i>
                        <span className="badge fw-semibold me-2" style={{ 
                          backgroundColor: COLORS.lightBg,
                          color: COLORS.primary,
                          padding: '4px 12px',
                          borderRadius: '6px'
                        }}>
                          {filteredProduits.length} produit(s)
                          {filteredProduits.length !== produits.length && 
                            ` sur ${produits.length} au total`}
                        </span>
                        
                        {hasActiveFilters() && (
                          <span>
                            <i className="bi bi-funnel me-1"></i>
                            Filtres actifs:
                            {search && <span className="badge bg-info ms-1" style={{ backgroundColor: COLORS.info }}>Recherche</span>}
                            {filterStatut && <span className="badge bg-info ms-1" style={{ backgroundColor: COLORS.info }}>Statut</span>}
                            {filterType && <span className="badge bg-info ms-1" style={{ backgroundColor: COLORS.info }}>Type</span>}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {loading || loadingTypes ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-2" style={{ color: COLORS.darkText }}>
                  {loadingTypes ? "Chargement des types de produits..." : "Chargement des produits..."}
                </p>
              </div>
            ) : produits.length === 0 && !abonnementError ? (
              <div className="text-center py-5">
                <div className="mb-4">
                  <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#F5F7FA',
                    color: '#B0BEC5'
                  }}>
                    <i className="bi bi-box-seam fs-1"></i>
                  </div>
                </div>
                <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucun produit trouvé</h6>
                <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                  Aucun produit n'a été enregistré pour le moment.
                </p>
                <div className="d-flex justify-content-center">
                  <button
                    onClick={() => navigate("/products/new")}
                    className="btn fw-semibold mt-3"
                    style={{
                      color: COLORS.white,
                      backgroundColor: COLORS.success,
                      borderColor: COLORS.success,
                      borderRadius: '8px',
                      padding: '10px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#43a047';
                      e.target.style.borderColor = '#43a047';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = COLORS.success;
                      e.target.style.borderColor = COLORS.success;
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <i className="bi bi-plus-lg me-2"></i> 
                    Ajouter un produit
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Affichage des résultats filtrés */}
                {filteredProduits.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                        width: '80px',
                        height: '80px',
                        backgroundColor: '#F5F7FA',
                        color: '#B0BEC5'
                      }}>
                        <i className="bi bi-search fs-1"></i>
                      </div>
                    </div>
                    <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucun résultat</h6>
                    <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                      Aucun produit ne correspond aux critères de recherche.
                    </p>
                      <div className="d-flex justify-content-center">
                          <button
                            className="btn fw-semibold mt-3"
                            onClick={resetFilters}
                            style={{
                              color: COLORS.primary,
                              borderColor: COLORS.primary,
                              backgroundColor: 'transparent',
                              borderRadius: '8px',
                              padding: '10px 24px',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = COLORS.primary;
                              e.target.style.color = COLORS.white;
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                              e.target.style.color = COLORS.primary;
                            }}
                          >
                            <i className="bi bi-x-lg me-2"></i> 
                            Effacer les filtres
                          </button>
                        </div>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead>
                          <tr style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderBottom: `2px solid ${COLORS.accent}`
                          }}>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Libellé</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Type</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Stock</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Statut</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Créé le</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px', textAlign: 'center' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.map((produit, index) => {
                            const status = statusLabel(produit.statut);
                            // Calculer le numéro de produit (en tenant compte de la pagination)
                            const productNumber = (currentPage - 1) * itemsPerPage + index + 1;

                            return (
                              <tr key={produit.id} style={{ borderBottom: `1px solid ${COLORS.lightBg}` }}>
                                <td style={{ padding: '16px' }}>
                                  <div className="d-flex align-items-center">
                                    <div>
                                      <div className="d-flex align-items-center mb-1">
                                        <span className="me-2" style={{ 
                                          color: COLORS.secondary,
                                          fontWeight: '600',
                                          fontSize: '0.9rem',
                                          minWidth: '24px'
                                        }}>
                                          {productNumber}.
                                        </span>
                                        <strong style={{ color: COLORS.darkText }}>{produit.libele}</strong>
                                      </div>
                                      <div className="small text-muted" style={{ fontSize: '0.8rem', marginTop: '4px', marginLeft: '28px' }}>
                                        {produit.updated_at && produit.updated_at !== 'null' && produit.updated_at !== '0000-00-00 00:00:00'
                                          ? `Dernière modif: ${formatDateShort(produit.updated_at)}`
                                          : `Créé le: ${formatDateShort(produit.created_at)}`}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  <span className="badge fw-semibold" style={{ 
                                    backgroundColor: '#E3F2FD',
                                    color: COLORS.primary,
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    fontSize: '0.85rem'
                                  }}>
                                    <i className="bi bi-tag me-1"></i>
                                    {typeProduitLabel(produit.type_produit || produit.id_type_produit)}
                                  </span>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  <div>
                                    <span style={{
                                      color: produit.stock_faible ? '#c62828' : COLORS.darkText,
                                      fontWeight: 'bold',
                                      fontSize: '1.1rem'
                                    }}>
                                      {produit.quantite_stock}
                                    </span>
                                    <span className="text-muted" style={{ fontSize: '0.9rem', marginLeft: '4px' }}>
                                      unités
                                    </span>
                                    {produit.stock_faible && (
                                      <div>
                                        <span className="badge" style={{ backgroundColor: '#ffebee', color: '#c62828', fontSize: '0.72rem' }}>
                                          <i className="bi bi-exclamation-triangle-fill me-1"></i>
                                          Stock faible
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  <span className="badge fw-semibold" style={{ 
                                    backgroundColor: produit.statut === 1 ? '#e8f5e9' : '#f5f5f5',
                                    color: produit.statut === 1 ? '#2e7d32' : '#757575',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '0.85rem'
                                  }}>
                                    {produit.statut === 1 ? (
                                      <>
                                        <i className="bi bi-check-circle me-1"></i>
                                        Actif
                                      </>
                                    ) : (
                                      <>
                                        <i className="bi bi-slash-circle me-1"></i>
                                        Inactif
                                      </>
                                    )}
                                  </span>
                                </td>
                                
                                <td style={{ padding: '16px' }}>
                                  <div style={{ color: COLORS.darkText, fontWeight: '500' }}>
                                    {formatDateShort(produit.created_at)}
                                  </div>
                                </td>
                                
                                <td style={{ padding: '16px', textAlign: 'center' }}>
                                  <div className="d-flex gap-2 justify-content-center">
                                    {/* Bouton Détail */}
                                    <button
                                      className="btn btn-outline-info fw-semibold"
                                      onClick={() => navigate(`/products/${produit.id}/detail`)}
                                      title="Voir le détail"
                                    >
                                      <i className="bi bi-eye"></i>
                                    </button>
                                    
                                    {/* Bouton Modifier */}
                                    <button
                                      className="btn btn-outline-success fw-semibold"
                                      onClick={() => navigate(`/products/${produit.id}/update`)}
                                      title="Modifier"
                                    >
                                      <i className="bi bi-pencil"></i>
                                    </button>
                                    
                                    {/* Bouton Activer/Désactiver */}
                                    <button
                                      className="btn btn-outline-warning fw-semibold"
                                      onClick={() => openConfirmModal(produit, produit.statut === 1 ? "désactiver" : "activer")}
                                      disabled={updatingId === produit.id}
                                      title={produit.statut === 1 ? "Désactiver" : "Activer"}
                                    >
                                      {updatingId === produit.id ? (
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                      ) : (
                                        <i className={`bi ${produit.statut === 1 ? 'bi-slash-circle' : 'bi-check-circle'}`}></i>
                                      )}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-4">
                        <div className="d-flex align-items-center">
                          <span className="me-2" style={{ color: COLORS.darkText }}>Afficher :</span>
                          <select 
                            className="form-select form-select-sm w-auto"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText,
                              borderRadius: '6px'
                            }}
                          >
                            <option value="5">5</option>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>
                          <span className="ms-3" style={{ color: COLORS.darkText }}>
                            <span className="badge fw-semibold me-2" style={{ 
                              backgroundColor: '#E3F2FD',
                              color: COLORS.primary,
                              padding: '4px 12px',
                              borderRadius: '6px'
                            }}>
                              {filteredProduits.length} produit(s)
                              {filteredProduits.length !== produits.length ? ' (filtrés)' : ''}
                            </span>
                          </span>
                        </div>
                        
                        <nav>
                          <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button 
                                className="page-link fw-semibold"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                style={{
                                  color: currentPage === 1 ? '#B0BEC5' : COLORS.primary,
                                  borderColor: '#e0e0e0',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  margin: '0 2px'
                                }}
                              >
                                <i className="bi bi-chevron-left"></i>
                              </button>
                            </li>
                            
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter(page => {
                                if (totalPages <= 7) return true;
                                return page === 1 || 
                                       page === totalPages || 
                                       (page >= currentPage - 1 && page <= currentPage + 1);
                              })
                              .map((page, index, array) => {
                                if (index > 0 && page > array[index - 1] + 1) {
                                  return (
                                    <React.Fragment key={`ellipsis-${page}`}>
                                      <li className="page-item disabled">
                                        <span className="page-link" style={{ borderColor: '#e0e0e0' }}>...</span>
                                      </li>
                                      <li className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                        <button 
                                          className="page-link fw-semibold"
                                          onClick={() => handlePageChange(page)}
                                          style={{
                                            backgroundColor: currentPage === page ? COLORS.primary : 'transparent',
                                            borderColor: currentPage === page ? COLORS.primary : '#e0e0e0',
                                            color: currentPage === page ? COLORS.white : COLORS.primary,
                                            padding: '6px 12px',
                                            borderRadius: '6px',
                                            margin: '0 2px',
                                            transition: 'all 0.3s ease'
                                          }}
                                          onMouseEnter={(e) => {
                                            if (currentPage !== page) {
                                              e.target.style.backgroundColor = '#E3F2FD';
                                              e.target.style.borderColor = COLORS.secondary;
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            if (currentPage !== page) {
                                              e.target.style.backgroundColor = 'transparent';
                                              e.target.style.borderColor = '#e0e0e0';
                                            }
                                          }}
                                        >
                                          {page}
                                        </button>
                                      </li>
                                    </React.Fragment>
                                  );
                                }
                                
                                return (
                                  <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                    <button 
                                      className="page-link fw-semibold"
                                      onClick={() => handlePageChange(page)}
                                      style={{
                                        backgroundColor: currentPage === page ? COLORS.primary : 'transparent',
                                        borderColor: currentPage === page ? COLORS.primary : '#e0e0e0',
                                        color: currentPage === page ? COLORS.white : COLORS.primary,
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        margin: '0 2px',
                                        transition: 'all 0.3s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        if (currentPage !== page) {
                                          e.target.style.backgroundColor = '#E3F2FD';
                                          e.target.style.borderColor = COLORS.secondary;
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (currentPage !== page) {
                                          e.target.style.backgroundColor = 'transparent';
                                          e.target.style.borderColor = '#e0e0e0';
                                        }
                                      }}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                );
                              })}
                            
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button 
                                className="page-link fw-semibold"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                style={{
                                  color: currentPage === totalPages ? '#B0BEC5' : COLORS.primary,
                                  borderColor: '#e0e0e0',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  margin: '0 2px'
                                }}
                              >
                                <i className="bi bi-chevron-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Modal de confirmation pour activer/désactiver */}
      {showConfirmModal && selectedProduct && (
        <div 
          className="modal fade show" 
          style={{ 
            display: 'block', 
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1050
          }}
        >
          <div 
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: '500px' }}
          >
            <div 
              className="modal-content"
              style={{
                borderRadius: '12px',
                border: 'none',
                boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
              }}
            >
              <div className="modal-header" style={{ 
                backgroundColor: COLORS.lightBg,
                borderBottom: `2px solid ${modalAction === "activer" ? COLORS.success : COLORS.warning}`,
                borderRadius: '12px 12px 0 0',
                padding: '20px'
              }}>
                <h5 className="modal-title" style={{ color: COLORS.primary, fontWeight: '600' }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                      width: '30px',
                      height: '30px',
                      backgroundColor: modalAction === "activer" ? COLORS.success : COLORS.warning,
                      color: 'white'
                    }}>
                      <i className={`bi ${modalAction === "activer" ? 'bi-check-circle' : 'bi-slash-circle'}`}></i>
                    </div>
                    Confirmation
                  </div>
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeConfirmModal}
                  disabled={updatingId === selectedProduct.id}
                ></button>
              </div>
              
              <div className="modal-body" style={{ padding: '24px' }}>
                <p className="mb-4" style={{ color: COLORS.darkText, fontSize: '1rem' }}>
                  Êtes-vous sûr de vouloir {modalAction} ce produit ?
                </p>
                
                <div className="p-4 rounded mb-4" style={{ 
                  backgroundColor: '#F8F9FA',
                  borderLeft: `4px solid ${modalAction === "activer" ? COLORS.success : COLORS.warning}`
                }}>
                  <div className="row">
                    <div className="col-6 mb-3">
                      <small className="text-muted d-block mb-1" style={{ fontSize: '0.85rem' }}>
                        Produit:
                      </small>
                      <div style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '1.1rem' }}>
                        {selectedProduct.libele}
                      </div>
                    </div>
                    <div className="col-6 mb-3">
                      <small className="text-muted d-block mb-1" style={{ fontSize: '0.85rem' }}>
                        Type:
                      </small>
                      <div style={{ color: COLORS.darkText, fontWeight: '500' }}>
                        {typeProduitLabel(selectedProduct.type_produit || selectedProduct.id_type_produit)}
                      </div>
                    </div>
                    <div className="col-6 mb-3">
                      <small className="text-muted d-block mb-1" style={{ fontSize: '0.85rem' }}>
                        Stock:
                      </small>
                      <div style={{ color: COLORS.darkText, fontWeight: '500' }}>
                        {selectedProduct.quantite_stock} unités
                      </div>
                    </div>
                    <div className="col-6 mb-3">
                      <small className="text-muted d-block mb-1" style={{ fontSize: '0.85rem' }}>
                        Statut actuel:
                      </small>
                      <span className="badge fw-semibold" style={{ 
                        backgroundColor: selectedProduct.statut === 1 ? '#e8f5e9' : '#f5f5f5',
                        color: selectedProduct.statut === 1 ? '#2e7d32' : '#757575',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '0.85rem'
                      }}>
                        {selectedProduct.statut === 1 ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="modal-footer" style={{ 
                backgroundColor: COLORS.lightBg,
                borderTop: `1px solid #dee2e6`,
                borderRadius: '0 0 12px 12px',
                padding: '20px'
              }}>
                <button
                  type="button"
                  className="btn fw-semibold"
                  onClick={closeConfirmModal}
                  disabled={updatingId === selectedProduct.id}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 24px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (updatingId !== selectedProduct.id) {
                      e.target.style.backgroundColor = '#E3F2FD';
                      e.target.style.borderColor = COLORS.secondary;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (updatingId !== selectedProduct.id) {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.borderColor = COLORS.primary;
                    }
                  }}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn fw-semibold"
                  onClick={handleToggleStatus}
                  disabled={updatingId === selectedProduct.id}
                  style={{
                    color: COLORS.white,
                    backgroundColor: modalAction === "activer" ? COLORS.success : COLORS.warning,
                    borderColor: modalAction === "activer" ? COLORS.success : COLORS.warning,
                    borderRadius: '8px',
                    padding: '10px 24px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (updatingId !== selectedProduct.id) {
                      e.target.style.backgroundColor = modalAction === "activer" ? '#43a047' : '#f57c00';
                      e.target.style.borderColor = modalAction === "activer" ? '#43a047' : '#f57c00';
                      e.target.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (updatingId !== selectedProduct.id) {
                      e.target.style.backgroundColor = modalAction === "activer" ? COLORS.success : COLORS.warning;
                      e.target.style.borderColor = modalAction === "activer" ? COLORS.success : COLORS.warning;
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {updatingId === selectedProduct.id ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Traitement...
                    </>
                  ) : (
                    `Confirmer ${modalAction}`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Styles CSS additionnels */}
      <style>{`

        .btn:focus,
        .form-control:focus,
        .page-link:focus,
        .form-select:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn,
        .badge,
        .page-link {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled,
        .page-link:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:focus,
        .form-select:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .table-hover tbody tr:hover {
          background-color: ${COLORS.lightBg};
          transition: background-color 0.2s ease;
        }
        
        .badge {
          font-weight: 500;
        }
        
        /* Styles spécifiques pour aligner les hauteurs */
        .form-control, .form-select {
          height: 38px !important;
          line-height: 1.5;
        }
        
        .input-group-text {
          height: 38px !important;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .btn {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .table-responsive {
            border: none;
          }
          
          .table {
            font-size: 0.875rem;
          }
          
          .table th,
          .table td {
            padding: 12px 8px !important;
          }
          
          .row.g-3 {
            margin-bottom: 8px;
          }
          
          .row.g-3 > div {
            margin-bottom: 8px;
          }
          
          .modal-dialog {
            margin: 16px;
            width: calc(100% - 32px);
          }
        }
        
        /* Amélioration de la lisibilité des tables */
        .table th {
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 0.85rem;
        }
        
        .table td {
          vertical-align: middle;
        }
        
        /* Animation pour les badges */
        .badge:hover {
          transform: translateY(-1px);
        }
        
        /* Styles pour les badges de statut */
        .badge.bg-success {
          background-color: ${COLORS.success} !important;
        }
        
        .badge.bg-warning {
          background-color: ${COLORS.warning} !important;
        }
        
        .badge.bg-info {
          background-color: ${COLORS.info} !important;
        }
      `}</style>
    </Layout>
  );
};

export default ProductList;